import { HStack, IconButton } from '@chakra-ui/react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { IoMdCheckmark } from 'react-icons/io';
import { LuMinus } from 'react-icons/lu';

import Tooltip from 'components/v4/Tooltip';
import ActionsCellWrapper from 'components/tables/AGGrid/ActionsCellWrapper';

const booleanCellRenderer = (params) =>
    params.value ? (
        <IoMdCheckmark fontSize="large" />
    ) : (
        <LuMinus fontSize="large" />
    );

const HEARTBEAT_WARNING_THRESHOLD_MS = 15 * 60 * 1000;
const HEARTBEAT_CRITICAL_THRESHOLD_MS = 30 * 60 * 1000;
const NEVER_ACTIVE_TIMESTAMP = '0001-01-01T00:00:00';

const HEARTBEAT_ROW_COLORS = {
    warning: {
        background: { light: '#FFFBEB', dark: 'rgba(251, 211, 141, 0.16)' },
        accent: '#F59E0B',
    },
    critical: {
        background: { light: '#FFF1F2', dark: 'rgba(254, 178, 178, 0.16)' },
        accent: '#F43F5E',
    },
};

/**
 * Builds an AG Grid `getRowStyle` callback that highlights an engine row once
 * its last heartbeat goes stale: an amber wash between 15-30 minutes,
 * escalating to rose past 30 minutes. Reinstates the legacy MUI Engines
 * table's red/yellow row indicator (ADO 2799) with a softer palette in place
 * of the old solid red/yellow fills.
 *
 * @param {boolean} darkMode Current dark mode state.
 * @returns {function} AG Grid `getRowStyle` callback.
 */
export const getEngineRowStyle = (darkMode) => (params) => {
    const lastActive = params.data?.lastActive;
    if (!lastActive || lastActive === NEVER_ACTIVE_TIMESTAMP) {
        return undefined;
    }

    const ageMs = Date.now() - new Date(`${lastActive}Z`).getTime();
    if (ageMs < HEARTBEAT_WARNING_THRESHOLD_MS) {
        return undefined;
    }

    const level =
        ageMs < HEARTBEAT_CRITICAL_THRESHOLD_MS ? 'warning' : 'critical';
    const { background, accent } = HEARTBEAT_ROW_COLORS[level];

    return {
        backgroundColor: darkMode ? background.dark : background.light,
        borderLeft: `4px solid ${accent}`,
    };
};

/**
 * Builds the AG Grid column definitions for the Engines table.
 *
 * @param {object}   params
 * @param {boolean}  params.darkMode      Current dark mode state.
 * @param {function} params.onViewEngine  Called with the row data when the view action is clicked.
 * @returns {Array} AG Grid column definitions.
 */
export const getEngineTableColumns = ({ darkMode, onViewEngine }) => [
    {
        headerName: 'ID',
        field: 'id',
        flex: 1,
        minWidth: 150,
    },
    {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        minWidth: 150,
        sort: 'asc',
    },
    {
        headerName: 'Last Heartbeat',
        field: 'lastActive',
        minWidth: 180,
        valueFormatter: (params) =>
            params.value ? new Date(params.value).toLocaleString() : '',
    },
    {
        headerName: 'Last Process Identifier',
        field: 'lastProcessIdentifier',
        minWidth: 200,
    },
    {
        headerName: 'Last Process Type',
        field: 'lastProcessType',
        minWidth: 180,
    },
    {
        headerName: 'Details',
        field: 'details',
        flex: 1,
        minWidth: 150,
    },
    {
        headerName: 'Active',
        field: 'isActive',
        width: 120,
        cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
        cellRenderer: booleanCellRenderer,
        valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
    },
    {
        headerName: 'Action',
        maxWidth: 100,
        filter: false,
        suppressMovable: true,
        lockVisible: true,
        suppressColumnsToolPanel: true,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        enableRowGroup: false,
        resizable: false,
        sortable: false,
        cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
        lockPosition: 'right',
        cellRenderer: (params) => (
            <ActionsCellWrapper>
                <HStack
                    spacing={0}
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    height="100%">
                    <Tooltip label="View Engine">
                        <IconButton
                            icon={
                                <BsThreeDotsVertical
                                    fill={darkMode ? 'white' : 'black'}
                                />
                            }
                            aria-label="View"
                            variant="ghost"
                            onClick={() => {
                                onViewEngine(params.data);
                            }}
                            _hover={{
                                bg: darkMode
                                    ? 'whiteAlpha.200'
                                    : 'blackAlpha.100',
                            }}
                            _active={{
                                bg: darkMode
                                    ? 'whiteAlpha.300'
                                    : 'blackAlpha.200',
                            }}
                            _focus={{ boxShadow: 'none' }}
                        />
                    </Tooltip>
                </HStack>
            </ActionsCellWrapper>
        ),
    },
];
